import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { Alert, Form } from "react-bootstrap";
import { TaxRate } from "shared/models-web";
import Popup from "../../components/ui/Popup";
import Table from "../../components/ui/Table";
import Store from "../../libs/store";
import { timeout } from "../../libs/utils/utils";

@observer
export default class TaxRates extends React.Component<{ store: Store }> {
    @observable
    private data: TaxRate[] = [];
    @observable
    hasError = false;
    @observable
    errorMessage = "";

    @observable
    showAddUpdateForm = false;
    popupHasError = false;
    @observable
    popupErrorMessage = "";

    @observable
    taxRate: TaxRate = {
        name: "",
        amount: 0
    };
    @observable
    taxRateId?: string;

    constructor(props: any) {
        super(props);

        this.init();
        makeObservable(this);
    }

    @action
    init = async () => {
        try {
            const data = await this.props.store.codetablesApi.list_taxrate();

            this.data = data.data.taxRates;
        } catch (rawE) {
            //Some idot at typescript made exception of type unknwon ...
            const e = rawE as any;

            if (e.response && e.response.status === 403) {
                if (!(await this.props.store.get_sessions().is_valid())) {
                    await this.props.store.logout();
                }
            }

            if (e.response && e.response.data) {
                console.log("here", e.response.data);
                this.errorMessage = e.response.data.errMsg;
            } else {
                this.errorMessage = `We got unspecified error: ${e}`;
            }

            this.hasError = true;

            await timeout(2000);
            this.hasError = false;
        }
    };

    @action
    load = async (taxRateId: string) => {
        try {
            const data = await this.props.store.codetablesApi.load_taxrate(taxRateId);

            this.taxRate = data.data.taxRate;
        } catch (rawE) {
            //Some idot at typescript made exception of type unknwon ...
            const e = rawE as any;

            if (e.response && e.response.status === 403) {
                if (!(await this.props.store.get_sessions().is_valid())) {
                    await this.props.store.logout();
                }
            }

            if (e.response && e.response.data) {
                console.log("here", e.response.data);
                this.popupErrorMessage = e.response.data.errMsg;
            } else {
                this.popupErrorMessage = `We got unspecified error: ${e}`;
            }

            this.popupHasError = true;

            await timeout(2000);
            this.popupHasError = false;
        }
    };

    @action
    onOpenAddEditTaxRate = async (taxRateId?: string) => {
        this.taxRateId = taxRateId;

        if (taxRateId) {
            await this.load(taxRateId);
        } else {
            this.taxRate = {
                name: "",
                amount: 0,
            };
        }

        this.showAddUpdateForm = true;
    };

    @action
    onCloseAddEditTaxRate = () => {
        this.showAddUpdateForm = false;
    };

    @action
    onSaveAddEditTaxRate = async () => {
        try {
            if (this.taxRateId) {
                await this.props.store.codetablesApi.update_taxrate(this.taxRateId, this.taxRate);
            } else {
                await this.props.store.codetablesApi.add_taxrate(this.taxRate);
            }

            this.showAddUpdateForm = false;
            await this.init();
        } catch (rawE) {
            //Some idot at typescript made exception of type unknwon ...
            const e = rawE as any;

            if (e.response && e.response.status === 403) {
                if (!(await this.props.store.get_sessions().is_valid())) {
                    await this.props.store.logout();
                }
            }

            if (e.response && e.response.data) {
                console.log("here", e.response.data);
                this.popupErrorMessage = e.response.data.errMsg;
            } else {
                this.popupErrorMessage = `We got unspecified error: ${e}`;
            }

            this.popupHasError = true;

            await timeout(2000);
            this.popupHasError = false;
        }
    };

    render(): React.ReactNode {
        return (
            <>
                <ul className="breadcrumb">
                    <li className="breadcrumb-item">HOME</li>
                    <li className="breadcrumb-item">CODETABLES</li>
                    <li className="breadcrumb-item active">TAX RATES</li>
                </ul>

                <h1 className="page-header">
                    Tax Rates <small>View the list and more</small>
                </h1>

                {this.hasError && <Alert variant="danger">{this.errorMessage}</Alert>}

                <div className="d-flex align-items-center mb-3">
                    <div className="btn-group">
                        <button type="button" className="btn btn-sm btn-outline-theme rounded-0" onClick={() => this.onOpenAddEditTaxRate()}>
                            <i className="fa fa-fw fa-plus" />
                            &nbsp;
                            <span>Add</span>
                        </button>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <Table
                            columns={[
                                {
                                    Header: "Name",
                                    accessor: "name",
                                    disableSortBy: false,
                                },
                                {
                                    Header: "Amount",
                                    accessor: "amount",
                                    disableSortBy: false,
                                },
                                {
                                    Header: "Text",
                                    accessor: "text",
                                    disableSortBy: false,
                                },
                                {
                                    Header: "Actions",
                                    accessor: "action",
                                    disableSortBy: true,
                                    actions: (row: any) => {
                                        return (
                                            <div className="btn-group btn-group-sm">
                                                <button type="button" className="btn btn-outline-theme btn-sm" onClick={() => this.onOpenAddEditTaxRate(row.original._id)}>
                                                    Edit
                                                </button>
                                                <button type="button" className="btn btn-outline-theme btn-sm">
                                                    Delete
                                                </button>
                                            </div>
                                        );
                                    },
                                },
                            ]}
                            data={this.data}
                        />
                    </div>
                    <div className="card-arrow">
                        <div className="card-arrow-top-left"></div>
                        <div className="card-arrow-top-right"></div>
                        <div className="card-arrow-bottom-left"></div>
                        <div className="card-arrow-bottom-right"></div>
                    </div>
                </div>


                <Popup title="Enter details " show={this.showAddUpdateForm} onClose={this.onCloseAddEditTaxRate} onSave={this.onSaveAddEditTaxRate}>
                    {this.popupHasError && <Alert variant="danger">{this.popupErrorMessage}</Alert>}

                    <Form.Group className="mb-3" controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" placeholder="Name" value={this.taxRate && this.taxRate.name} onChange={action((e) => this.taxRate && (this.taxRate.name = e.target.value))} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="text">
                        <Form.Label>Text</Form.Label>
                        <Form.Control type="text" placeholder="Text" value={this.taxRate && this.taxRate.text} onChange={action((e) => this.taxRate && (this.taxRate.text = e.target.value))} />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="amount">
                        <Form.Label>Amount</Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="Amount"
                            value={this.taxRate && this.taxRate.amount}
                            onChange={action((e) => {
                                const amount = Number.parseFloat(e.target.value);

                                if (this.taxRate && !isNaN(amount)) {
                                    this.taxRate.amount = amount;
                                }
                            })}
                        />
                    </Form.Group>
                </Popup>
            </>
        );
    }
}
